import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import { FormProvider, SubmitHandler, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Button, Box, Container, Theme, useMediaQuery, Typography, Alert, Snackbar, IconButton, Tooltip, DialogContent, DialogTitle, Dialog, DialogActions, Divider } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createMergeFieldMapOptions, createTemplateListOptions } from '@/services/template/notification.options';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import { LoadingButton } from '@mui/lab';
import { DeleteIcon } from 'lucide-react';
import ConfirmModal from '@/components/Molecules/Modal/ConfirmModal.component';
import { ContentCopy } from '@mui/icons-material';
import useNotification, { createTemplateDownloadByTemplateIdOptions } from '@/services/template/notification2.options';
import saveAs from 'file-saver';
import FileInput from '@/components/Molecules/FilePicker/FileInput.component';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

const templateSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  description: z.string().optional(),
  file: z.any().optional()
});

const initialTemplate = {
  title: '',
  description: '',
  file: null as File | null
}

type MergeField = {
  MergeFieldId: number;
  MergeFieldName: string;
  CustomFieldId: number;
  CustomFieldName: string;
};

type Template = z.infer<typeof templateSchema>;

const TemplateDrawer = () => {
  const navigate = useNavigate();
  const params = useParams({ from: '/_auth/templates/$templateId' });
  const { variant, customerId, title } = useSearch({ from: '/_auth/templates/$templateId' })
  const [activeStep, setActiveStep] = useState(0);

  const mergeFieldMap = useQuery(createMergeFieldMapOptions(customerId))
  const templateDownload = useQuery(createTemplateDownloadByTemplateIdOptions(params?.templateId))
  const allTemplates = useQuery(createTemplateListOptions(customerId))

  const { createTemplate, modifyTemplate, deleteTemplate } = useNotification()

  const methods = useForm<Template>({
    mode: 'all',
    defaultValues: initialTemplate,
    // shouldUnregister: false,
    resolver: zodResolver(templateSchema)
  });

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));


  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [openTemplateFieldsDialog, setOpenTemplateFieldsDialog] = useState(false);

  // Open drawer when component mounts
  useEffect(() => {
    setIsOpen(true)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate({ to: '/templates' })
    }, 300) // Match this with MUI's transition duration
  }

  const handleCloseDrawer = () => {
    handleClose()
    setActiveStep(0);
    // methods.reset(editCustomerValues2);
    navigate({ to: '/templates' });
  };

  const handleSubmit: SubmitHandler<any> = async (formValues: any) => {
    const formData = new FormData();

    if (formValues.file) {
      formData.append('template', formValues.file);
    }

    formData.append('CustomerId', `${customerId}`);
    formData.append('TemplateName', formValues.title || '');
    formData.append('Description', formValues.description || '');

    if (variant === 'edit') {
      formData.append('TemplateId', params.templateId.toString());
      try {
        await modifyTemplate({
          actionType: "modifyTemplate",
          data: formData,
        });
        handleCloseDrawer()
      } catch (error) {
        console.error("Create new template failed", error);
      }
    } else {
      try {
        await createTemplate({
          actionType: "createTemplate",
          data: formData,
        });
        handleCloseDrawer()
      } catch (error) {
        console.error("Create new template failed", error);
      }
    }
  }

  const handleComplete = () => {
    console.log('handleComplete')
    console.log(methods.getValues())
  }

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplate({
        actionType: "deleteTemplate",
        templateId: params.templateId.toString(),
      });
      handleCloseDrawer()
    } catch (error) {
      console.error("Create new template failed", error);
    }
  };

  const handleDownload = () => {
    // Extract the filename from the Content-Disposition header
    const disposition = templateDownload?.data?.headers['content-disposition'];

    // Split the disposition to find the filename
    let filename = "template.docx"; // default filename
    if (disposition && disposition.indexOf('filename=') !== -1) {
      const filenamePart = disposition.split('filename=')[1];
      filename = filenamePart.trim().replace(/['"]/g, '');
    }

    const blob = new Blob([templateDownload?.data?.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    saveAs(blob, filename);
  }

  useEffect(() => {
    if (variant === 'edit') {

      const fileData = allTemplates?.data?.find((template: any) => template.TemplateId === Number(params?.templateId))//?.TemplateFile
      const file = new File(
        [''],
        fileData.FileName,
        {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }
      );

      methods.setValue('file', file);
      methods.setValue('title', title)
    }
  }, [variant, title])

  const TemplateFieldsDialog = () => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleClose = () => {
      setOpenTemplateFieldsDialog(false);
    };


    const handleCopy = (text: any) => {
      navigator.clipboard.writeText(text).then(() => {
        setSnackbarOpen(true);
      });
    };

    const handleSnackbarClose = () => {
      setSnackbarOpen(false);
    };

    return (
      <>
        <Dialog open={openTemplateFieldsDialog} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">Merge Fields</DialogTitle>
          <DialogContent>
            <h4 style={{ marginBottom: '8px' }}>
              Below are the list of merge fields available to use inside your merge document.
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '32px', rowGap: '8px', alignItems: 'center' }}>
              <div style={{ fontWeight: 'bold', textAlign: 'left', textDecoration: 'underline' }}>Base/Custom Field Name</div>
              <div style={{ fontWeight: 'bold', textAlign: 'left', textDecoration: 'underline' }}>Merge Field Name</div>
              {mergeFieldMap.data?.map((field: MergeField, index: number) => (
                <React.Fragment key={index}>
                  <div style={{ textAlign: 'left' }}>{field.CustomFieldName}:</div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }}>{field.MergeFieldName}</span>
                    <Tooltip title="Copy to clipboard">
                      <IconButton size="small" onClick={() => handleCopy(field.MergeFieldName)}>
                        <ContentCopy style={{ fontSize: '14px' }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant='outlined'>
              Close
            </Button>
          </DialogActions>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            message="Copied to clipboard"
          />
        </Dialog>
      </>
    )
  }

  return (
    <DrawerLayout
      open={isOpen}
      onClose={handleCloseDrawer}
      anchor="right"
      keepMounted
      width={'50%'}
      Header={
        <DrawerHeader
          close={handleCloseDrawer}
          title={variant === 'edit' ? 'Edit Template' : 'New Template'}
        />
      }
    >
      <Grid
        container
        style={{
          height: '100%',
          width: '100%',
        }}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent={'space-between'}
        flexWrap="nowrap"
        padding={4}
        overflow="auto"
      >
        <FormProvider {...methods}>
          {/* @ts-ignore */}
          <Container maxWidth='xl' component='form' onSubmit={methods.handleSubmit(handleSubmit)} sx={{
            width: '75%',
            overflowY: 'auto',
            mt: 5,
            height: '98%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            mb: 4,
          }}>
            <Box sx={{ mb: 4 }}>
              {variant === 'new' && (
                <>
                  <Box sx={{ mb: 4 }}>

                    <Alert severity='info'>

                      <Typography variant='subtitle1'>
                        A template consists of a Microsoft Word mail merge document. The extension of the document must be .docx and the file must be saved as a Word document not a Word template format. To see a list of available fields {" "}
                        <a onClick={() => setOpenTemplateFieldsDialog(true)} style={{ cursor: 'pointer', textDecoration: 'underline' }}><b>click here.</b></a>
                      </Typography>
                    </Alert>
                  </Box>

                </>

              )}
              <TextBox
                name='title'
                fullWidth
                label='Template Title'
                sx={{ mb: 4, mt: 2 }}
              />
              {variant === 'new' && (
                <Box>
                  <Typography variant='h6'>
                    Upload Template File
                  </Typography>
                  <FileInput
                    name="file"
                    control={methods.control}
                    placeholder="Select File"
                    hideSizeText
                    fullWidth
                    InputProps={{
                      inputProps: {
                        accept: '.docx',
                        onError: (e) => console.log(e),
                      },
                      startAdornment: <AttachFileIcon />,
                    }}
                    clearIconButtonProps={{
                      title: 'Remove',
                      children: <CloseIcon fontSize="small" />,
                    }}
                  />
                </Box>
              )}

              {variant === 'edit' && (
                <>
                  <Box>
                    <Typography variant='h6'>
                      Upload Template File
                    </Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      Uploading a new file will replace the current template and the old one will be overwritten. <b>This cannot be undone.</b>
                    </Typography>
                    <FileInput
                      name="file"
                      control={methods.control}
                      placeholder="Select File"
                      hideSizeText
                      fullWidth
                      InputProps={{
                        inputProps: {
                          accept: '.docx',
                          onError: (e) => console.log(e),
                        },
                        startAdornment: <AttachFileIcon />,
                      }}
                      clearIconButtonProps={{
                        title: 'Remove',
                        children: <CloseIcon fontSize="small" />,
                      }}
                    />
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Typography variant='h6' sx={{ mb: 2 }}>
                      Download Template File
                    </Typography>
                    <LoadingButton
                      loading={templateDownload?.isLoading}
                      disabled={!templateDownload?.data}
                      onClick={handleDownload}
                      variant='outlined'
                    >
                      Download
                    </LoadingButton>
                  </Box>
                </>
              )}
            </Box>
            <Box>
              <Divider />
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  // backgroundColor: 'white',
                  // borderTop: '1px solid',
                  // borderColor: 'grey.300',
                  p: 5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem'
                }}>
                <LoadingButton
                  loading={isDeleting}
                  color="error"
                  startIcon={<DeleteIcon />}
                  size='small'
                  onClick={() => setOpenModal(true)}>
                  Delete
                </LoadingButton>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={handleCloseDrawer} size='small'>
                    Cancel & Close
                  </Button>
                  {openModal && (
                    <ConfirmModal
                      open={openModal}
                      setOpen={setOpenModal}
                      title="Are you sure?"
                      content="This action will permanently delete this template."
                      onSuccess={handleDelete}
                      onClose={() => setOpenModal(false)}
                      cancelButtonText="Cancel"
                      confirmButtonText="Delete"
                    />
                  )}
                  <LoadingButton loading={isUpdating} variant="contained" type="submit" size='small'>
                    Save
                  </LoadingButton>
                </Box>
              </Box>
            </Box>
            <TemplateFieldsDialog />
          </Container>

        </FormProvider>
      </Grid>
    </DrawerLayout >
  )
}

export default TemplateDrawer